import React from 'react';
import SwContext from '.';
import config from '../../../config';
import useIsInIframe, { useIsFromApp } from '../../Hooks/useIsInIframe';
import {
  fetchPublishedVersion,
  readCurrentDocumentVersion,
  refreshCurrentDocument,
  VERSION_POLL_INTERVAL_MS,
} from './versionPolling';

function isStandaloneDisplayMode() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches;
}

function isRunningAsPwa() {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };

  return isStandaloneDisplayMode()
    || navigatorWithStandalone.standalone === true;
}

function EmbeddedAppSwContextProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [needRefresh, setNeedRefresh] = React.useState(false);
  const currentVersion = React.useMemo(
    () => readCurrentDocumentVersion() || config.AppVersion,
    [],
  );

  const refresh = React.useCallback(() => {
    setNeedRefresh(false);
    refreshCurrentDocument();
  }, [needRefresh]);

  const checkForUpdates = React.useCallback(async () => {
    if (config.Environment === 'development') return;
    if (needRefresh) return;

    try {
      const publishedVersion = await fetchPublishedVersion();
      if (publishedVersion && publishedVersion !== currentVersion) {
        setNeedRefresh(true);
      }
    } catch (error) {
      console.error('Embedded app version check failed', error);
    }
  }, [currentVersion, needRefresh]);

  React.useEffect(() => {
    checkForUpdates();

    const interval = window.setInterval(() => {
      checkForUpdates();
    }, VERSION_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [checkForUpdates]);

  const value = React.useMemo(() => ({
    needRefresh,
    refresh,
  }), [needRefresh, refresh]);

  return (
    <SwContext.Provider value={value}>
      {children}
    </SwContext.Provider>
  );
}

const PwaSwContextProvider = React.lazy(() => import('./PwaSwContextProvider'));

export default function SwContextProviderWrapper(props: { children: React.ReactNode }) {
  const { children } = props;
  const isInIframe = useIsInIframe();
  const isFromApp = useIsFromApp();
  const isPwaMode = React.useMemo(() => isRunningAsPwa(), []);
  const defaultValue = React.useMemo(() => ({ needRefresh: false, refresh() { } }), []);

  // Unregister any lingering service workers when running inside the Electron app.
  // A previously installed SW can intercept navigations and serve stale cached assets
  // from an older version even after the Electron app has updated to a new URL path.
  React.useEffect(() => {
    if (isFromApp && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((r) => r.unregister());
      });
    }
  }, [isFromApp]);

  if ((import.meta.env.DEV && import.meta.env.MODE !== 'test') || isInIframe) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <SwContext.Provider value={defaultValue}>{children}</SwContext.Provider>;
  }
  // isFromApp must be checked before isPwaMode: Electron windows can match
  // standalone display-mode, which would wrongly activate PwaSwContextProvider.
  if (isFromApp) {
    return <EmbeddedAppSwContextProvider {...props} />;
  }
  if (isPwaMode) {
    return (
      <React.Suspense fallback={<SwContext.Provider value={defaultValue}>{children}</SwContext.Provider>}>
        <PwaSwContextProvider {...props} />
      </React.Suspense>
    );
  }
  return <SwContext.Provider value={defaultValue}>{children}</SwContext.Provider>;
}
